import { HousingRule } from "../constants/housingRules";
import { parseX500Name } from "../utils/common";
import { ApproverDetails } from "./claim";

import {
    BankAccount,
    BeneficiaryDetails,
    GenericState,
    ISODate,
    LinearId,
    MOCK_BANK,
    MOCK_BENE_BANK_ACCOUNT,
    MOCK_CONTRACTOR,
    MOCK_DEVELOPER,
    MOCK_PAYER_BANK_ACCOUNT,
    UUID,
    X500Name,
    HistoryItem,
} from "./common";

export type AccountInfo = {
    accountName: string;
    accountNumber: string;
    addressLn1?: string;
    addressLn2?: string;
    bic?: string;
    ultimateName?: string;
};

export const beneficiaryDetailsToAccountInfo = (
    bene?: BeneficiaryDetails,
): AccountInfo => ({
    accountName: bene?.bankAccountDetails.accountName ?? "",
    accountNumber: bene?.bankAccountDetails.accountNumber ?? "",
    addressLn1: bene?.bankAccountDetails.bankAddress,
    addressLn2: bene?.bankAccountDetails.country,
    bic: bene?.bankAccountDetails.bankCode,
    ultimateName: bene?.name,
});

export const bankAccountToAccountInfo = (bene?: BankAccount): AccountInfo => ({
    accountName: bene?.bankAccountHolderName ?? "",
    accountNumber: bene?.bankAccountNumber ?? "",
    addressLn1: bene?.registeredAddress,
    addressLn2: bene?.registeredAddress,
    bic: bene?.swiftAddress,
    ultimateName: "",
});

export type Participant = {
    addressLn1?: string;
    addressLn2?: string;
    bic?: string;
    identity?: X500Name;
    legalName: string;
    lei: string;
    wallet?: string;
    externalRef?: string;
};

export type PaymentState = {
    bankInformation: {
        bankInfos: string[];
    };
    chargeBearer: string;
    customerRefNumber: string;
    entryDate: ISODate;
    fromAccount: AccountInfo;
    instructedMVCurrency: string;
    instructedMVUnit: number;
    linearId: LinearId;
    payee: Participant;
    payer: Participant;
    paymentType: string;
    purposeCode: {
        purposeCode: string;
    };
    rejectCode?: string;
    rejectDate?: string;
    rejectReason?: string;
    remittanceInformation: {
        narratives: string[];
    };
    settlementBank: X500Name;
    settlementBankRef?: string;
    settlementDate?: ISODate;
    status: PaymentStatus | null;
    toAccount: AccountInfo;
    valueDate: ISODate;
};

export type PreSubmitPaymentData = {
    bankInformation: string[];
    chargeBearer: string;
    customerRefNumber: string;
    director: string;
    fromAccount: AccountInfo;
    instructedMVCurrency: string;
    instructedMVUnit: number;
    payee: Participant;
    payer: Participant;
    paymentType: string;
    purposeCode: string;
    remittanceInformation: string[];
    settlementBank: X500Name;
    toAccount: AccountInfo;
    valueDate: ISODate;
};

export type SharedDocument = {
    documentName: string;
    documentHash: string;
};

export type PaymentStateWithDocuments = {
    approver: string;
    paymentState: PaymentState;
    sharedDocument: SharedDocument[];
};

export type PreAndPaymentStateWithDocuments = {
    paymentOrderState: PreSubmitPaymentState;
    sharedDocument: SharedDocument[];
    paymentState: PaymentState | null;
};

export enum PaymentStatus {
    PENDING = "PAYMENT_PENDING",
    SUBMITTED = "PAYMENT_REQUEST",
    COMPLETED = "PAYMENT_COMPLETED",
    FAILED = "PAYMENT_FAILED",
}

export type PreSubmitPaymentProps = {
    bankInformation: string[];
    chargeBearer: string;
    customerRefNumber: string;
    fromAccount: AccountInfo;
    instructedMVCurrency: string;
    instructedMVUnit: number;
    payee: Participant;
    payer: Participant;
    paymentType: string;
    preparer: string;
    purposeCode: string;
    remittanceInformation: string[];
    settlementBank: X500Name;
    toAccount: AccountInfo;
    valueDate: ISODate;
    paymentLinearId?: LinearId | null;
    invoiceLinearId: LinearId;
    approvers: ApproverDetails[];
};

export enum PaymentOrderStatus {
    PARTIAL_APPROVED = "PARTIAL_APPROVED",
    CREATED = "CREATED",
    REJECTED = "REJECTED",
    ALL_APPROVED = "ALL_APPROVED",
}

export type PreSubmitPaymentState = GenericState<
    PreSubmitPaymentProps,
    PaymentOrderStatus
>;

export type PaymentInfo = {
    paymentOrderStatus: PaymentOrderStatus;
    paymentStatus: PaymentStatus | null;
    paymentOrderApprovers: ApproverDetails[];
};

export type PaymentReceiptState = {
    referenceId: string;
    transactionRefNum: string;
    entryDate: ISODate;
    payee: Participant;
    toAccount: AccountInfo;
    amountMinor: number,
    currency: string;
    relatedReference: string;
    orderingCustomer: string;
    orderingInstitution: string;
    remittanceInformation: {
        narratives: string[];
    };
    valueDate: string;
};

export enum PaymentReceiptStatus {
    PAYMENT_RECEIPT_REQUEST = "PAYMENT_RECEIPT_REQUEST",
};

export type PreSubmitHistory = {
    paymentId: string;
    currentStatus: PaymentOrderStatus;
    statusHistory: HistoryItem<PaymentOrderStatus>[];
};

export type PaymentOrderApprover = {
    paymentOrderCustomerRef: string;
    referenceId: string;
    projectReferenceId: string;
    claimReferenceId: string;
    invoiceReferenceId: string;
    externalProjectId: string;
    projectName: string;
    contractorAccountName: string;
    contractorDisplayName?: string;
    contractorWallet: string;
    status: number;
    createdAt: string;
    updatedAt: string;
};



export const MOCK_PRE_SUBMIT_PAYMENT_PROPS: PreSubmitPaymentProps = {
    bankInformation: [HousingRule.PARAGRAPH_5E, HousingRule.PARAGRAPH_5A],
    chargeBearer: "SHAR",
    customerRefNumber: "some-customer-ref",
    preparer: "some preparaer",
    fromAccount: beneficiaryDetailsToAccountInfo(MOCK_PAYER_BANK_ACCOUNT),
    instructedMVCurrency: "SGD",
    instructedMVUnit: 8922,
    payee: {
        identity: MOCK_CONTRACTOR[0].party,
        legalName: parseX500Name(MOCK_CONTRACTOR[0].party).get("O") ?? "",
        lei: "",
        bic: MOCK_BENE_BANK_ACCOUNT.bankAccountDetails.bankCode,
        addressLn1: MOCK_BENE_BANK_ACCOUNT.address,
    },
    payer: {
        identity: MOCK_DEVELOPER.party,
        legalName: parseX500Name(MOCK_DEVELOPER.party).get("O") ?? "",
        lei: "",
        bic: MOCK_PAYER_BANK_ACCOUNT.bankAccountDetails.bankCode,
        addressLn1: MOCK_PAYER_BANK_ACCOUNT.address,
    },
    paymentType: "-",
    purposeCode: "SUPP",
    remittanceInformation: [
        "Lorem ipsum dolor sit amet, consectet ur adipiscing elit. Vehicula in pur us, lacus, odio. Rhoncus sapien, am et pulvinar pharetra. Convallis et.",
    ],
    settlementBank: MOCK_BANK,
    toAccount: beneficiaryDetailsToAccountInfo(MOCK_BENE_BANK_ACCOUNT),
    valueDate: "2021-11-02T04:18:43.299Z",
    invoiceLinearId: {
        externalId: "",
        id: "4b74f9f8-2702-425c-863b-c6bd044905de",
    },
    paymentLinearId: null,
    approvers: [
        {
            details: {
                roleName: "r2",
                firstName: "Minh",
                lastName: "Payment",
                email: "minhpaymentorder@gmail.com",
                userProfileName: "Approver Entity",
            },
            actionTimestamp: "2023-06-15T08:27:11.255Z",
            status: undefined,
        },
    ],
};

export const MOCK_PRE_SUBMIT_PAYMENT_LIST: PreSubmitPaymentState[] = [
    {
        linearId: {
            id: "uuid-1",
        },
        createdAt: "2021-11-02T04:18:43.299Z",
        status: PaymentOrderStatus.CREATED,
        props: MOCK_PRE_SUBMIT_PAYMENT_PROPS,
        lastChangedTime: "2021-11-02T04:18:43.299Z",
        comments: [],
    },
    {
        linearId: {
            id: "uuid-2",
        },
        createdAt: "2021-11-02T04:18:43.299Z",
        status: PaymentOrderStatus.PARTIAL_APPROVED,
        props: MOCK_PRE_SUBMIT_PAYMENT_PROPS,
        lastChangedTime: "2021-11-02T04:18:43.299Z",
        comments: [],
    },
    {
        linearId: {
            id: "uuid-3",
        },
        createdAt: "2021-11-02T04:18:43.299Z",
        status: PaymentOrderStatus.REJECTED,
        props: MOCK_PRE_SUBMIT_PAYMENT_PROPS,
        lastChangedTime: "2021-11-02T04:18:43.299Z",
        comments: [
            {
                fullName: "ca name",
                email: "ca@ca.com",
                comment: "Approver rejected",
                resolved: false,
                created: "2023-06-21T10:07:12.646Z",
            },
        ],
    },
];

export const MOCK_PRE_SUBMIT_PAYMENT_HISTORY: {
    [key in PaymentOrderStatus]: PreSubmitPaymentState[];
} = {
    [PaymentOrderStatus.CREATED]: [MOCK_PRE_SUBMIT_PAYMENT_LIST[0]],
    [PaymentOrderStatus.PARTIAL_APPROVED]: [
        MOCK_PRE_SUBMIT_PAYMENT_LIST[1],
        MOCK_PRE_SUBMIT_PAYMENT_LIST[0],
    ],
    [PaymentOrderStatus.REJECTED]: [
        MOCK_PRE_SUBMIT_PAYMENT_LIST[2],
        MOCK_PRE_SUBMIT_PAYMENT_LIST[0],
    ],
    [PaymentOrderStatus.ALL_APPROVED]: [],
};

export const MOCK_PRE_SUBMIT_PAYMENT_HISTORY_MAP: Map<
    UUID,
    PreSubmitPaymentState[]
> | null = new Map([
    ["uuid-1", MOCK_PRE_SUBMIT_PAYMENT_HISTORY[PaymentOrderStatus.CREATED]],
    [
        "uuid-2",
        MOCK_PRE_SUBMIT_PAYMENT_HISTORY[PaymentOrderStatus.PARTIAL_APPROVED],
    ],
    ["uuid-3", MOCK_PRE_SUBMIT_PAYMENT_HISTORY[PaymentOrderStatus.REJECTED]],
]);

export const MOCK_PAYMENT: PaymentState = {
    bankInformation: {
        bankInfos: [HousingRule.PARAGRAPH_5E, HousingRule.PARAGRAPH_5A],
    },
    chargeBearer: "SHAR",
    customerRefNumber: "some-customer-ref",
    entryDate: "2021-11-02T04:18:43.299Z",
    fromAccount: beneficiaryDetailsToAccountInfo(MOCK_PAYER_BANK_ACCOUNT),
    instructedMVCurrency: "SGD",
    instructedMVUnit: 8922,
    linearId: {
        id: "some-uuid-1",
    },
    payee: {
        identity: MOCK_CONTRACTOR[0].party,
        legalName: parseX500Name(MOCK_CONTRACTOR[0].party).get("O") ?? "",
        lei: "",
        bic: MOCK_BENE_BANK_ACCOUNT.bankAccountDetails.bankCode,
        addressLn1: MOCK_BENE_BANK_ACCOUNT.address,
    },
    payer: {
        identity: MOCK_DEVELOPER.party,
        legalName: parseX500Name(MOCK_DEVELOPER.party).get("O") ?? "",
        lei: "",
        bic: MOCK_PAYER_BANK_ACCOUNT.bankAccountDetails.bankCode,
        addressLn1: MOCK_PAYER_BANK_ACCOUNT.address,
    },
    paymentType: "-",
    purposeCode: {
        purposeCode: "SUPP",
    },
    remittanceInformation: {
        narratives: [
            "Lorem ipsum dolor sit amet, consectet ur adipiscing elit. Vehicula in pur us, lacus, odio. Rhoncus sapien, am et pulvinar pharetra. Convallis et.",
        ],
    },
    settlementBank: MOCK_BANK,
    status: PaymentStatus.SUBMITTED,
    toAccount: beneficiaryDetailsToAccountInfo(MOCK_BENE_BANK_ACCOUNT),
    valueDate: "2021-11-02T04:18:43.299Z",
};

export const MOCK_PAYMENT_LIST: PaymentState[] = [
    MOCK_PAYMENT,
    {
        ...MOCK_PAYMENT,
        linearId: {
            id: "some-uuid-2",
        },
        status: PaymentStatus.PENDING,
    },
    {
        ...MOCK_PAYMENT,
        linearId: {
            id: "some-uuid-3",
        },
        status: PaymentStatus.COMPLETED,
        settlementDate: "2021-11-02T04:18:43.299Z",
        settlementBankRef: "some-bank-ref",
    },
    {
        ...MOCK_PAYMENT,
        linearId: {
            id: "some-uuid-4",
        },
        status: PaymentStatus.FAILED,
        rejectDate: "2021-11-02T04:18:43.299Z",
        rejectReason: "some reason",
    },
];

export const MOCK_PRE_SUBMIT_HISTORY_ITEMS: HistoryItem<PaymentOrderStatus>[] = [
    {
        status: PaymentOrderStatus.CREATED,
        timestamp: "2026-07-13T05:14:19Z",
    },
    {
        status: PaymentOrderStatus.PARTIAL_APPROVED,
        timestamp: "2026-07-13T05:16:27Z",
    },
    {
        status: PaymentOrderStatus.ALL_APPROVED,
        timestamp: "2026-07-13T05:20:51Z",
    }
];

export const MOCK_PRE_SUBMIT_HISTORY: PreSubmitHistory = {
    paymentId: "9",
    currentStatus: PaymentOrderStatus.ALL_APPROVED,
    statusHistory: MOCK_PRE_SUBMIT_HISTORY_ITEMS,
};

// export const MOCK_PAYMENT_HISTORY_MAP: Map<UUID, PaymentState[]> | null =
//     new Map([
//         ["some-uuid-1", MOCK_PAYMENT_HISTORY[PaymentStatus.SUBMITTED]],
//         ["some-uuid-2", MOCK_PAYMENT_HISTORY[PaymentStatus.PENDING]],
//         ["some-uuid-3", MOCK_PAYMENT_HISTORY[PaymentStatus.COMPLETED]],
//         ["some-uuid-4", MOCK_PAYMENT_HISTORY[PaymentStatus.FAILED]],
//     ]);

export const MOCK_DOCUMENTS: SharedDocument[] = [
    {
        documentHash: "some-doc-hash",
        documentName: "some-doc-name.pdf",
    },
];

export const MOCK_PRE_AND_PAYMENT_LIST: PreAndPaymentStateWithDocuments[] = [
    // pre submit payment created
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[0],
        paymentState: null,
        sharedDocument: MOCK_DOCUMENTS,
    },
    // pre submit payment rejected
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[2],
        paymentState: null,
        sharedDocument: MOCK_DOCUMENTS,
    },
    // pre submit payment approved, payment submitted
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[1],
        paymentState: MOCK_PAYMENT_LIST[0],
        sharedDocument: MOCK_DOCUMENTS,
    },
    // pre submit payment approved, payment pending
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[1],
        paymentState: MOCK_PAYMENT_LIST[1],
        sharedDocument: MOCK_DOCUMENTS,
    },
    // pre submit payment approved, payment approved
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[1],
        paymentState: MOCK_PAYMENT_LIST[2],
        sharedDocument: MOCK_DOCUMENTS,
    },
    // pre submit payment approved, payment rejected
    {
        paymentOrderState: MOCK_PRE_SUBMIT_PAYMENT_LIST[1],
        paymentState: MOCK_PAYMENT_LIST[3],
        sharedDocument: MOCK_DOCUMENTS,
    },
];

export const MOCK_PAYMENT_RECEIPT: PaymentReceiptState = {
    referenceId: "b606875c-5cbe-84ed-9bce-b024d7e3f12d",
    transactionRefNum: "ADVS3H26082101",
    entryDate: "2026-08-21T07:17:08Z",
    payee: {
        wallet: "0x000000000000000000000000000000000000dead",
        legalName: "TKH-Service-Provider-001-kevin-001-test",
        addressLn1: "",
        addressLn2: "",
        bic: "",
        lei: "",
        externalRef: "0xE4fc1b06F91bb70Dcf1e3601935e14a1FA504636"
    },
    toAccount: {
        accountName: "TKH-Service-Provider-001-kevin-001-test",
        accountNumber: "147-153571-001",
        addressLn1: "",
        addressLn2: "",
        bic: "",
        ultimateName: ""
    },
    amountMinor: 40000,
    currency: "SGD",
    relatedReference: "LISA81108360000",
    orderingCustomer: "",
    orderingInstitution: "",
    remittanceInformation: {
        narratives: []
    },
    valueDate: "221114"
};

export const MOCK_PAYMENT_RECEIPT_LIST: PaymentReceiptState[] = [
    MOCK_PAYMENT_RECEIPT,
    {
        ...MOCK_PAYMENT_RECEIPT,
        entryDate: "2021-11-03T04:18:43.299Z",
        referenceId: "some-uuid-2",
        transactionRefNum: "some-txn-ref-num-2",
        amountMinor: 23333,
        currency: "USD",
    },
    {
        ...MOCK_PAYMENT_RECEIPT,
        entryDate: "2021-11-04T04:18:43.299Z",
        referenceId: "some-uuid-3",
        transactionRefNum: "some-txn-ref-num-3",
        amountMinor: 400000,
    },
];
